/*
 * Purpose: Flow-scoped controls for returning users — check, edit, duplicate, share, run, stop
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Play,
  Save,
  Square,
  ClipboardCheck,
  Pencil,
  Copy,
  Share2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { WorkflowSelector } from '@/components/WorkflowSelector';
import { WorkflowCloneDialog } from '@/components/WorkflowCloneDialog';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowValidator } from '@/services/validation';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { Switch } from '@/components/ui/switch';
import { missionStartBlockedReason, isMapLocked } from '@/lib/missionRules';
import { toast } from 'sonner';

export interface FlowControlBarProps {
  isExecuting: boolean;
  onExecute: () => void | Promise<void>;
  onStop: () => void;
}

export function FlowControlBar({ isExecuting, onExecute, onStop }: FlowControlBarProps) {
  const { currentWorkflow, saveWorkflow, updateWorkflowName, updateMissionMeta, selectNode } = useWorkflowStore();
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [cloneOpen, setCloneOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);

  const validation = useMemo(() => {
    if (!currentWorkflow) return [];
    return WorkflowValidator.validate(currentWorkflow);
  }, [currentWorkflow]);

  const errorCount = validation.filter((e) => e.type === 'error').length;
  const warningCount = validation.filter((e) => e.type === 'warning').length;
  const startBlocked = missionStartBlockedReason(currentWorkflow);
  const startTitle =
    startBlocked ??
    (errorCount > 0 ? 'Fix check issues before starting' : undefined);

  const mapLocked = isMapLocked(currentWorkflow);

  const handleMapLockChange = useCallback(
    (locked: boolean) => {
      updateMissionMeta({
        mapLocked: locked,
        allowDynamicMap: !locked,
      });
      saveWorkflow();
      toast.message(locked ? 'MapLock on — map stays fixed during runs' : 'Map unlocked — AI may replan mid-run');
    },
    [updateMissionMeta, saveWorkflow]
  );

  const handleSave = useCallback(() => {
    saveWorkflow();
    toast.success(`${PRODUCT_TERMS.autoBlockFlow} saved`);
  }, [saveWorkflow]);

  const openEdit = useCallback(() => {
    if (!currentWorkflow) return;
    setEditName(currentWorkflow.name);
    setEditOpen(true);
  }, [currentWorkflow]);

  const handleRename = useCallback(() => {
    if (!editName.trim()) {
      toast.error('Enter a flow name');
      return;
    }
    updateWorkflowName(editName);
    saveWorkflow();
    toast.success('Flow renamed');
    setEditOpen(false);
  }, [editName, updateWorkflowName, saveWorkflow]);

  const handleShare = useCallback(async () => {
    if (!currentWorkflow) return;
    const url = `${window.location.origin}/builder?flow=${currentWorkflow.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  }, [currentWorkflow]);

  const statusBadge = !currentWorkflow ? null : isExecuting ? (
    <Badge variant="secondary" className="shrink-0 text-xs">
      Running
    </Badge>
  ) : errorCount > 0 ? (
    <Badge variant="destructive" className="shrink-0 text-xs">
      {errorCount} issue{errorCount === 1 ? '' : 's'}
    </Badge>
  ) : warningCount > 0 ? (
    <Badge variant="outline" className="shrink-0 text-xs border-amber-500/50 text-amber-700 dark:text-amber-400">
      {warningCount} warning{warningCount === 1 ? '' : 's'}
    </Badge>
  ) : (
    <Badge variant="outline" className="shrink-0 text-xs border-green-500/40 text-green-700 dark:text-green-400">
      Ready
    </Badge>
  );

  return (
    <>
      <div className="shrink-0 flex flex-col gap-2 px-3 sm:px-4 py-2 border-b border-border bg-card/80 backdrop-blur-sm rounded-t-lg">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <WorkflowSelector />
          {statusBadge}
          {currentWorkflow && (
            <p className="hidden lg:block text-xs text-muted-foreground truncate ml-auto">
              {currentWorkflow.nodes.length} blocks · {currentWorkflow.edges.length} links
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Popover open={checkOpen} onOpenChange={setCheckOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!currentWorkflow}
                className="ui-lift-subtle"
              >
                <ClipboardCheck className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Check</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="start">
              <p className="text-sm font-medium mb-2">Flow check</p>
              {validation.length === 0 ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 ui-icon-ok shrink-0" />
                  All checks passed — safe to run.
                </p>
              ) : (
                <ul className="space-y-2 max-h-48 overflow-auto">
                  {validation.map((item, idx) => (
                    <li
                      key={`${item.message}-${idx}`}
                      className="text-xs flex items-start gap-2"
                    >
                      {item.type === 'error' ? (
                        <AlertCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 ui-icon shrink-0 mt-0.5" />
                      )}
                      <span className="flex-1">{item.message}</span>
                      {item.nodeId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-[10px]"
                          onClick={() => {
                            selectNode(item.nodeId!);
                            setCheckOpen(false);
                          }}
                        >
                          Go
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="sm"
            disabled={!currentWorkflow}
            onClick={openEdit}
            className="ui-lift-subtle"
          >
            <Pencil className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Edit</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            disabled={!currentWorkflow}
            onClick={() => setCloneOpen(true)}
            className="ui-lift-subtle"
          >
            <Copy className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Duplicate</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            disabled={!currentWorkflow}
            onClick={() => void handleShare()}
            className="ui-lift-subtle"
          >
            <Share2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Share</span>
          </Button>

          {currentWorkflow && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-muted/30"
              title={
                mapLocked
                  ? 'MapLock on — block map cannot change during a run'
                  : 'Map unlocked — AI may add or change blocks between START and GOAL while running'
              }
            >
              <Lock className={`w-3.5 h-3.5 shrink-0 ${mapLocked ? 'text-primary' : 'text-muted-foreground'}`} />
              <Label htmlFor="map-lock" className="text-xs cursor-pointer whitespace-nowrap">
                MapLock
              </Label>
              <Switch
                id="map-lock"
                checked={mapLocked}
                disabled={isExecuting}
                onCheckedChange={handleMapLockChange}
                aria-label="MapLock — prevent dynamic map changes during run"
              />
            </div>
          )}

          <div className="flex-1 min-w-[8px]" />

          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={!currentWorkflow || isExecuting}
            className="ui-lift-subtle"
          >
            <Save className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Save</span>
          </Button>

          {isExecuting ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={onStop}
              className="ui-lift"
            >
              <Square className="w-4 h-4 sm:mr-1.5 fill-current" />
              <span className="hidden sm:inline">Stop</span>
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => void onExecute()}
              disabled={!currentWorkflow || errorCount > 0}
              title={startTitle}
              className="ui-lift ui-lift-accent"
            >
              <Play className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Start</span>
              <span className="sm:hidden">Run</span>
            </Button>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {PRODUCT_TERMS.autoBlockFlow}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="flow-name">Flow name</Label>
            <Input
              id="flow-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="ui-form-control"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Modify blocks on the map below. Use Check before starting.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename}>Save name</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {currentWorkflow && (
        <WorkflowCloneDialog
          workflowId={currentWorkflow.id}
          workflowName={currentWorkflow.name}
          open={cloneOpen}
          onOpenChange={setCloneOpen}
        />
      )}
    </>
  );
}
